import {
  Button,
  ButtonLevel,
  ButtonSize,
  Menu,
  MenuItem,
  MenuPopover,
  MenuTrigger,
  Text,
  TextColor,
  TextSize,
} from '@vizit/atoms';
import { ChevronDownIcon, PlusIcon } from '@vizit/icons';
import * as React from 'react';

interface NewProductMenuButtonProps {
  /**
   * Dialog component for creating a new product page
   * Must support isOpen, onOpenChange, and hideTrigger props
   */
  createProductDialog?: React.ReactNode;
  /**
   * Dialog component for importing a product page via URL
   * Must support isOpen, onOpenChange, and hideTrigger props
   */
  importProductDialog?: React.ReactNode;
  /**
   * Dialog component for bulk importing product pages via CSV
   * Must support isOpen, onOpenChange, and hideTrigger props
   */
  bulkImportDialog?: React.ReactNode;
}

type DialogType = 'create' | 'import' | 'bulk';

interface ControlledDialogProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

function NewProductMenuButton(props: NewProductMenuButtonProps) {
  const { createProductDialog, importProductDialog, bulkImportDialog } = props;

  const [openDialog, setOpenDialog] = React.useState<DialogType | null>(null);

  const hasAnyDialog = createProductDialog || importProductDialog || bulkImportDialog;

  if (!hasAnyDialog) {
    return null;
  }

  const handleOpenCreate = () => setOpenDialog('create');
  const handleOpenImport = () => setOpenDialog('import');
  const handleOpenBulk = () => setOpenDialog('bulk');

  const handleDialogOpenChange = (type: DialogType) => (open: boolean) => {
    if (!open) {
      setOpenDialog(null);
    } else {
      setOpenDialog(type);
    }
  };

  const renderControlledDialog = (dialog: React.ReactNode, type: DialogType) => {
    if (!dialog || !React.isValidElement(dialog)) return null;

    return React.cloneElement(dialog as React.ReactElement<ControlledDialogProps>, {
      isOpen: openDialog === type,
      onOpenChange: handleDialogOpenChange(type),
      hideTrigger: true,
    });
  };

  return (
    <>
      <MenuTrigger>
        <Button
          level={ButtonLevel.Primary}
          size={ButtonSize.Lg}
          iconEnd={<ChevronDownIcon size={16} />}
          iconStart={<PlusIcon size={16} />}
          data-testid="library-products-new-product-button"
        >
          New product page
        </Button>
        <MenuPopover placement="bottom end">
          <Menu>
            {importProductDialog && (
              <MenuItem
                onAction={handleOpenImport}
                textValue="Score a product page"
                data-testid="library-products-score-new"
              >
                <Text size={TextSize.Md} color={TextColor.Inherit}>
                  Score a product page
                </Text>
              </MenuItem>
            )}
            {createProductDialog && (
              <MenuItem onAction={handleOpenCreate} textValue="Stage new product page">
                <Text size={TextSize.Md} color={TextColor.Inherit}>
                  Stage new product page
                </Text>
              </MenuItem>
            )}
            {bulkImportDialog && (
              <MenuItem
                onAction={handleOpenBulk}
                textValue="Bulk score product pages"
                data-testid="library-products-bulk-score"
              >
                <Text size={TextSize.Md} color={TextColor.Inherit}>
                  Bulk score product pages
                </Text>
              </MenuItem>
            )}
          </Menu>
        </MenuPopover>
      </MenuTrigger>

      {renderControlledDialog(createProductDialog, 'create')}
      {renderControlledDialog(importProductDialog, 'import')}
      {renderControlledDialog(bulkImportDialog, 'bulk')}
    </>
  );
}

export { NewProductMenuButton };
export type { NewProductMenuButtonProps };
